import { IDocumentAssemblerGenerationPlaceholder } from '@commenda/api';
import {
  Badge,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import moment from 'moment';
import { KeyboardEvent } from 'react';

import { DocumentAssemblerHelper } from './DocumentAssemblerHelper';

interface DocumentAssemblerGenerationPlaceholderChipProps {
  placeholder: IDocumentAssemblerGenerationPlaceholder;
}

const PENDING_PREFIX = 'PENDING:';

const onChipActivateKey = (event: KeyboardEvent<HTMLSpanElement>): void => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  event.preventDefault();
  event.currentTarget.click();
};

const chipClassName = (
  placeholder: IDocumentAssemblerGenerationPlaceholder
): string => {
  if (DocumentAssemblerHelper.isLeftOut(placeholder)) {
    return 'text-text-quaternary line-through';
  }
  if (DocumentAssemblerHelper.isPending(placeholder)) {
    return 'font-medium text-text-warning';
  }
  // An undecided slot may already carry a value, so this has to be explicit: without it the value
  // falls through to the success style while the popover badge reads "Branch not decided".
  if (DocumentAssemblerHelper.isUndecided(placeholder)) {
    return 'font-medium text-text-warning';
  }

  return 'font-medium text-text-success';
};

export const DocumentAssemblerGenerationPlaceholderChip = ({
  placeholder,
}: DocumentAssemblerGenerationPlaceholderChipProps) => {
  const isPending = DocumentAssemblerHelper.isPending(placeholder);
  const isLeftOut = DocumentAssemblerHelper.isLeftOut(placeholder);
  const isUndecided = DocumentAssemblerHelper.isUndecided(placeholder);
  const badge = DocumentAssemblerHelper.getPlaceholderAnswerBadge(placeholder);

  const label = () => {
    const { tag } = placeholder.placeholdersSurroundingContext;

    if (isPending) return `${PENDING_PREFIX}${tag}`;
    if (placeholder.value === null) return tag;
    if (placeholder.value === '') return 'written as empty';

    return placeholder.value;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onKeyDown={onChipActivateKey}
          className={cn(
            'cursor-pointer whitespace-pre-wrap hover:underline',
            placeholder.value === '' && !isPending && 'italic',
            chipClassName(placeholder)
          )}
        >
          {label()}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex flex-col gap-md">
        <div className="flex items-center justify-between gap-md">
          <span className="txt-extra-small-medium text-text-tertiary">
            Slot {placeholder.ordinal + 1}
          </span>
          <Badge variant={badge.variant} size="sm">
            {badge.label}
          </Badge>
        </div>
        <div className="flex flex-col gap-xxs">
          <span className="txt-extra-small-medium text-text-tertiary">
            The instruction that asked for it
          </span>
          <p className="txt-small-regular text-text-secondary">
            {placeholder.instruction}
          </p>
        </div>
        {placeholder.writtenAt ? (
          <span className="txt-extra-small-regular text-text-tertiary">
            Written {moment(placeholder.writtenAt).format('lll')}
          </span>
        ) : null}
        {isPending ? (
          <p className="txt-extra-small-regular text-text-tertiary">
            Nothing answered this, so the document carries the instruction here,
            highlighted yellow, for someone to complete by hand.
          </p>
        ) : null}
        {isLeftOut ? (
          <p className="txt-extra-small-regular text-text-tertiary">
            The branch this sits in was not chosen, so this passage is absent
            from the document.
          </p>
        ) : null}
        {isUndecided ? (
          <p className="txt-extra-small-regular text-text-tertiary">
            No decision was recorded for the branch this sits in, so whether
            this passage belongs in the document is still open.
          </p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
